import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Upload, X, Star, MapPin, AlertTriangle,
  FileText, Trash2, Loader2, Building2, Home, Map as MapIcon,
  Fence, Lock, Zap, Droplet, Waves, Route, Car, ArrowUpDown, Dumbbell,
  Shield, Battery, Video, Coffee, Sprout, Navigation, LocateFixed
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatApiError, API_BASE, formatPrice, prefixUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const leafletMarkerIcon = typeof window !== "undefined" && L && L.icon ? L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
}) : undefined;

// ---------- Constants (architecture spec §24) ----------
const STEPS = [
  { id: 1, key: "basic", label: "Basic Info" },
  { id: 2, key: "location", label: "Location" },
  { id: 3, key: "details", label: "Property Details" },
  { id: 4, key: "amenities", label: "Amenities" },
  { id: 5, key: "images", label: "Images" },
  { id: 6, key: "documents", label: "Documents" },
  { id: 7, key: "review", label: "Review & Submit" },
];

const SUBTYPES = [
  // Apartment / Flat
  { value: "1_RK_STUDIO", label: "1 RK / Studio", category: "FLAT", group: "Apartment / Flat" },
  { value: "1_BHK", label: "1 BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "2_BHK", label: "2 BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "3_BHK", label: "3 BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "4_PLUS_BHK", label: "4+ BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "PENTHOUSE", label: "Penthouse", category: "FLAT", group: "Apartment / Flat" },
  
  // House / Villa
  { value: "INDEPENDENT_HOUSE", label: "Independent House", category: "HOUSE", group: "House / Villa" },
  { value: "VILLA", label: "Villa", category: "VILLA", group: "House / Villa" },
  { value: "BUILDER_FLOOR", label: "Builder Floor", category: "HOUSE", group: "House / Villa" },
  { value: "DUPLEX", label: "Duplex", category: "HOUSE", group: "House / Villa" },
  { value: "FARM_HOUSE", label: "Farm House", category: "HOUSE", group: "House / Villa" },
  
  // Plot / Land
  { value: "RESIDENTIAL_PLOT", label: "Residential Plot", category: "PLOT", group: "Plot / Land" },
  { value: "COMMERCIAL_PLOT", label: "Commercial Plot", category: "PLOT", group: "Plot / Land" },
  { value: "AGRICULTURAL_LAND", label: "Agricultural Land", category: "LAND", group: "Plot / Land" },
  { value: "INDUSTRIAL_PLOT", label: "Industrial Plot", category: "PLOT", group: "Plot / Land" }
];

const CATEGORIES = [
  { value: "LAND", label: "Land" },
  { value: "PLOT", label: "Plot" },
  { value: "HOUSE", label: "House" },
  { value: "VILLA", label: "Villa" },
  { value: "FLAT", label: "Flat / Apartment" },
  { value: "COMMERCIAL", label: "Commercial" },
];
const LAND_CATS = new Set(["LAND", "PLOT"]);
const LISTING_TYPES = [{ value: "SELL", label: "Sell" }, { value: "RENT", label: "Rent" }];
const AREA_UNITS = ["sq_yard", "sq_meter", "sq_ft", "acre", "hectare", "bigha", "katha"];
const FACING_OPTS = ["North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"];
const OWNERSHIP = ["freehold", "leasehold", "co_operative", "power_of_attorney"];
const FURNISHING = ["Furnished", "Semi-Furnished", "Unfurnished"];
const AMENITIES = [
  "Boundary Wall", "Gated Colony", "Electricity", "Water", "Drainage",
  "Road Access", "Parking", "Lift", "Gym", "Swimming Pool",
  "Security", "Power Backup", "CCTV", "Club House", "Garden",
];
const AMENITY_ICONS = {
  "Boundary Wall": Fence,
  "Gated Colony": Lock,
  "Electricity": Zap,
  "Water": Droplet,
  "Drainage": Waves,
  "Road Access": Route,
  "Parking": Car,
  "Lift": ArrowUpDown,
  "Gym": Dumbbell,
  "Swimming Pool": Waves,
  "Security": Shield,
  "Power Backup": Battery,
  "CCTV": Video,
  "Club House": Coffee,
  "Garden": Sprout,
};
const LAND_DOC_TYPES = [
  { key: "registry", label: "Registry" },
  { key: "jamabandi", label: "Jamabandi" },
  { key: "khatiyan", label: "Khatiyan" },
  { key: "mutation", label: "Mutation" },
  { key: "khasra", label: "Khasra" },
  { key: "survey_number", label: "Survey Number" },
  { key: "ownership_proof", label: "Ownership Proof" },
  { key: "tax_receipt", label: "Tax Receipt" },
];

const BUILDING_DOC_TYPES = [
  { key: "registry", label: "Registry / Title Deed" },
  { key: "ownership_proof", label: "Ownership Proof" },
];

const MIN_IMAGES = 5;

// Issue #31: shared guards so every numeric field rejects negative values.
// blockNegativeKeys stops the user typing "-"/"e"/"+" and shows immediate inline
// feedback; clampNonNegative strips a negative value that slips in via paste.
const blockNegativeKeys = (e) => {
  if (["-", "e", "E", "+"].includes(e.key)) {
    e.preventDefault();
    toast.error("Only 0 or positive numbers are allowed", { id: "numeric-negative" });
  }
};
const clampNonNegative = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return n < 0 ? "0" : value;
};

// ---------- Leaflet helpers ----------
function MapClick({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng.lat, e.latlng.lng) });
  return null;
}
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center?.[0] && center?.[1]) map.setView(center, map.getZoom()); }, [center, map]);
  return null;
}

// ---------- Component ----------
export default function PropertyRegistrationWizard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const typeParam = queryParams.get("type");
  const editId = queryParams.get("edit");

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(Boolean(editId));
  const [existingError, setExistingError] = useState("");
  const existingImagesRef = useRef(new Set());
  const existingDocsRef = useRef(new Map());
  // Reveal inline validation errors only after the user tries to advance a step.
  const [attempted, setAttempted] = useState(false);
  useEffect(() => { setAttempted(false); }, [step]);

  // Validate-on-click: advance when valid, otherwise surface inline errors + toast.
  const handleNext = () => {
    if (step === 2 && !locationConfirmed) {
      if (!loc.latitude || !loc.longitude) {
        toast.error("Please select a location on the map first");
        return;
      }
      setLocationConfirmed(true);
      return;
    }
    if (canNext) {
      setAttempted(false);
      setStep((s) => s + 1);
    } else {
      setAttempted(true);
      toast.error("Please complete the highlighted required fields");
    }
  };

  // Step 1
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("PLOT");
  const [subtype, setSubtype] = useState("RESIDENTIAL_PLOT");
  const [listingType, setListingType] = useState("SELL");
  
  const isApartment = ["1_RK_STUDIO", "1_BHK", "2_BHK", "3_BHK", "4_PLUS_BHK", "PENTHOUSE"].includes(subtype);
  const isHouse = ["INDEPENDENT_HOUSE", "VILLA", "BUILDER_FLOOR", "DUPLEX", "FARM_HOUSE"].includes(subtype);
  const isLand = LAND_CATS.has(category);

  useEffect(() => {
    if (typeParam === "land") {
      setSubtype("RESIDENTIAL_PLOT");
      setCategory("PLOT");
    } else if (typeParam === "house") {
      setSubtype("INDEPENDENT_HOUSE");
      setCategory("HOUSE");
    } else if (typeParam === "flat") {
      setSubtype("1_BHK");
      setCategory("FLAT");
    }
  }, [typeParam]);

  const filteredAmenities = useMemo(() => {
    if (isLand) {
      // Issue #30: "Gated Colony" is not applicable to plot/land listings.
      return ["Boundary Wall", "Electricity", "Water", "Drainage", "Road Access"];
    }
    const base = [
      "Electricity", "Water", "Drainage", "Parking", "Lift", "Gym", "Swimming Pool",
      "Security", "Power Backup", "CCTV", "Club House", "Garden"
    ];
    // Issue #30: only residential building types (Apartment, Villa, Independent
    // House, Builder Floor, etc.) expose "Gated Colony".
    if (isApartment || isHouse) {
      return ["Gated Colony", ...base];
    }
    return base;
  }, [isLand, isApartment, isHouse]);

  // Issue #18: plots/land are sale-only on this platform. Never offer Rent and
  // force the listing type to SELL whenever a land/plot category is selected.
  const availableListingTypes = useMemo(
    () => (isLand ? [{ value: "SELL", label: "Sell" }] : LISTING_TYPES),
    [isLand]
  );
  useEffect(() => {
    if (isLand && listingType !== "SELL") {
      setListingType("SELL");
    }
  }, [isLand, listingType]);

  const [description, setDescription] = useState("");
  const [highlights, setHighlights] = useState("");
  const [price, setPrice] = useState("");

  // Step 2
  const [loc, setLoc] = useState({
    country: "India", state: "", district: "", city: "", sector: "",
    locality: "", address: "", pincode: "", latitude: null, longitude: null,
    house_number: "", landmark: ""
  });
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [locMethod, setLocMethod] = useState("map_pin"); // "map_pin" | "current_location" | "pincode_search"
  const [isLocating, setIsLocating] = useState(false);
  const [geolocationError, setGeolocationError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showFullAddress, setShowFullAddress] = useState(false);
  const [pincodeError, setPincodeError] = useState(false);
  // Issue #29 (Option 1): search by PIN code and auto-populate locality/city/district/state.
  const [pinLookup, setPinLookup] = useState("");
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState("");
  const [pinLocalities, setPinLocalities] = useState([]);

  // Step 3 - land details
  const [land, setLand] = useState({
    area: "", area_unit: "sq_yard", facing: "East",
    road_width_ft: "", corner_plot: false,
    registry_available: false, mutation_available: false,
    khatiyan: "", jamabandi: "", khasra_number: "", survey_number: "",
    ownership_type: "freehold",
  });
  // Step 3 - building details
  const [building, setBuilding] = useState({
    bedrooms: 0, bathrooms: 0, balconies: 0,
    furnishing: "Semi-Furnished", property_age_years: 0,
    floor_number: "", total_floors: "", parking: 0, lift: false,
  });

  // Step 4
  const [amenities, setAmenities] = useState([]);
  // Step 5
  const [images, setImages] = useState([]);
  const [imageThumbnails, setImageThumbnails] = useState({});
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [uploadingImg, setUploadingImg] = useState(false);
  // Step 6
  const [docs, setDocs] = useState({}); // {docType: {path, uploaded_at}}
  const [uploadingDoc, setUploadingDoc] = useState("");

  // Resume an owned/assigned draft through the same seven-step form. The
  // protected property endpoint enforces row ownership before returning draft
  // data; this client never trusts an ID from the query string by itself.
  useEffect(() => {
    if (!editId) {
      setLoadingExisting(false);
      return;
    }
    let active = true;
    setLoadingExisting(true);
    setExistingError("");
    api.get(`/properties/${editId}`)
      .then(({ data }) => {
        if (!active) return;
        const property = data?.property || data || {};
        const raw = property.raw && typeof property.raw === "object" ? property.raw : {};
        const landDetails = property.land_details || property.land || {};
        const buildingDetails = property.building_details || property.building || {};
        const latitude = property.latitude ?? property.geo_lat ?? null;
        const longitude = property.longitude ?? property.geo_lng ?? null;
        setPropertyId(property.property_id || property.id || editId);
        setTitle(property.title || "");
        setDescription(property.description || "");
        setCategory(property.category || "PLOT");
        setSubtype(raw.subcategory || raw.property_type || "RESIDENTIAL_PLOT");
        setListingType(property.listing_type || "SELL");
        setPrice(property.price == null ? "" : String(property.price));
        setHighlights(Array.isArray(raw.highlights) ? raw.highlights.join(", ") : "");
        setLoc((current) => ({
          ...current,
          country: property.country || "India",
          state: property.state || "",
          district: property.district || "",
          city: property.city || "",
          sector: property.sector || "",
          locality: property.locality || "",
          address: property.address || "",
          pincode: property.pincode || "",
          latitude,
          longitude,
        }));
        setLocationConfirmed(latitude != null && longitude != null);
        setLand((current) => ({ ...current, ...landDetails,
          area: property.area ?? landDetails.area ?? "",
          area_unit: property.area_unit || landDetails.area_unit || current.area_unit,
        }));
        setBuilding((current) => ({ ...current, ...buildingDetails }));
        setAmenities(Array.isArray(property.amenities) ? property.amenities : []);
        const existingImages = Array.isArray(property.images)
          ? property.images.map((image) => prefixUrl(typeof image === "string" ? image : image?.url)).filter(Boolean)
          : [];
        existingImagesRef.current = new Set(existingImages);
        setImages(existingImages);
        const loadedDocs = {};
        const originalDocs = new Map();
        if (Array.isArray(property.documents)) {
          property.documents.forEach((document) => {
            const key = document.document_type;
            const path = prefixUrl(document.url || document.file_url);
            if (!key || !path) return;
            loadedDocs[key] = { path, name: document.file_name || `${key}.pdf`, existing: true };
            originalDocs.set(key, path);
          });
        }
        existingDocsRef.current = originalDocs;
        setDocs(loadedDocs);
      })
      .catch((error) => {
        if (active) setExistingError(formatApiError(error) || "This listing could not be opened for editing.");
      })
      .finally(() => { if (active) setLoadingExisting(false); });
    return () => { active = false; };
  }, [editId]);

  useEffect(() => {
    if (isLand) {
      setAmenities((current) => current.filter((item) => item !== "Gated Colony"));
    }
  }, [isLand]);


  // ---------- Validation per step ----------
  const stepErrors = useMemo(() => {
    const errs = {};
    if (step >= 1) {
      if (!title.trim()) errs.basic_title = "Title is required";
      if (!price || Number(price) <= 0) errs.basic_price = "Valid price required";
      if (!description.trim()) errs.basic_desc = "Description required";
    }
    if (step >= 2) {
      if (!locationConfirmed) errs.loc_pin = "Please confirm the location on the map first";
      if (!loc.latitude || !loc.longitude) errs.loc_coords = "Map location coordinates are required";
      if (!loc.pincode || !loc.pincode.trim()) errs.loc_pincode = "Pincode is required and must be detected from the map location";
      if (!loc.state || !loc.state.trim()) errs.loc_state = "State is required";
      if (!loc.city || !loc.city.trim()) errs.loc_city = "City is required";
      if (!loc.locality || !loc.locality.trim()) errs.loc_locality = "Locality is required";
    }
    if (step >= 3) {
      if (isLand) {
        if (!land.area || Number(land.area) <= 0) errs.det_area = "Area required";
      } else {
        if (!building.bedrooms || Number(building.bedrooms) <= 0) errs.det_bedrooms = "Bedrooms count required";
        if (!building.bathrooms || Number(building.bathrooms) <= 0) errs.det_bathrooms = "Bathrooms count required";
        if (isApartment && (!building.floor_number || Number(building.floor_number) < 0)) errs.det_floor = "Floor number required";
        if (isApartment && (!building.total_floors || Number(building.total_floors) <= 0)) errs.det_total_floors = "Total floors required";
        if (isHouse && (!building.total_floors || Number(building.total_floors) <= 0)) errs.det_total_floors = "Total floors required";
      }
    }
    if (step >= 5) {
      if (images.length < MIN_IMAGES) errs.img_count = `Minimum ${MIN_IMAGES} images required`;
    }
    if (step >= 6 && isLand) {
      const missingDocs = LAND_DOC_TYPES.filter((d) => !docs[d.key]);
      if (missingDocs.length) errs.doc_count = `Required land documents missing: ${missingDocs.map((d) => d.label).join(", ")}`;
    }
    return errs;
  }, [step, title, price, description, loc, locationConfirmed, isLand, land, images, docs, isApartment, isHouse, building]);

  const canNext = useMemo(() => {
    if (step === 1) return !stepErrors.basic_title && !stepErrors.basic_price && !stepErrors.basic_desc;
    if (step === 2) return !stepErrors.loc_pin && !stepErrors.loc_coords && !stepErrors.loc_pincode && !stepErrors.loc_state && !stepErrors.loc_city && !stepErrors.loc_locality;
    if (step === 3) {
      if (isLand) return !stepErrors.det_area;
      if (isApartment) return !stepErrors.det_bedrooms && !stepErrors.det_bathrooms && !stepErrors.det_floor && !stepErrors.det_total_floors;
      return !stepErrors.det_bedrooms && !stepErrors.det_bathrooms && !stepErrors.det_total_floors;
    }
    if (step === 5) return !stepErrors.img_count;
    if (step === 6) return !stepErrors.doc_count;
    return true;
  }, [step, stepErrors, isLand, isApartment]);

  // ---------- Image upload ----------
  const MAX_IMAGES = 20;
  const MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB
  const MAX_IMAGE_WIDTH = 12000;
  const MAX_IMAGE_HEIGHT = 12000;
  const MAX_IMAGE_PIXELS = 50_000_000;

  const readImageDimensions = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dimensions = { width: img.width, height: img.height };
      URL.revokeObjectURL(objectUrl);
      resolve(dimensions);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Invalid image data"));
    };
    img.src = objectUrl;
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed per property`);
      e.target.value = "";
      return;
    }

    const batch = files.slice(0, remaining);
    if (batch.length < files.length) {
      toast.warning(`Only ${remaining} more image(s) allowed. ${files.length - batch.length} file(s) were skipped.`);
    }

    const oversized = batch.filter((f) => f.size > MAX_IMAGE_SIZE);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} file(s) exceed the 15 MB limit: ${oversized.map((f) => f.name).join(", ")}`);
    }

    const sizeEligibleFiles = batch.filter((f) => f.size <= MAX_IMAGE_SIZE);
    if (!sizeEligibleFiles.length) {
      e.target.value = "";
      return;
    }

    const dimensionRejected = [];
    const validFiles = [];
    for (const file of sizeEligibleFiles) {
      try {
        const { width, height } = await readImageDimensions(file);
        const pixels = width * height;
        if (width > MAX_IMAGE_WIDTH || height > MAX_IMAGE_HEIGHT || pixels > MAX_IMAGE_PIXELS) {
          dimensionRejected.push(`${file.name} (${width}x${height})`);
        } else {
          validFiles.push(file);
        }
      } catch {
        dimensionRejected.push(`${file.name} (invalid image content)`);
      }
    }

    if (dimensionRejected.length > 0) {
      toast.error(`Image dimensions/content invalid for ${dimensionRejected.length} file(s): ${dimensionRejected.join(", ")}`);
    }
    if (!validFiles.length) {
      e.target.value = "";
      return;
    }

    setUploadingImg(true);
    let succeededCount = 0;
    let failedCount = 0;

    try {
      for (const f of validFiles) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("asset_type", "property_image");
          const { data } = await api.post("/upload", fd);
          const rawUrl = data?.url || data?.file_url || data?.public_url || data?.download_url;
          const url = prefixUrl(rawUrl);
          const thumbnail = prefixUrl(data?.thumbnail_url);
          if (!url) throw new Error("Upload response missing image URL");
          setImages((prev) => [...prev, url]);
          if (thumbnail) {
            setImageThumbnails((current) => ({ ...current, [url]: thumbnail }));
          }
          succeededCount++;
        } catch (err) {
          failedCount++;
          const sizeMb = (f.size / (1024 * 1024)).toFixed(2);
          toast.error(`Failed to upload ${f.name} (${sizeMb} MB): ${formatApiError(err)}`);
        }
      }
      if (succeededCount > 0) {
        toast.success(`${succeededCount} image(s) uploaded successfully`);
      }
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };
  const removeImage = (idx) => {
    const removedUrl = images[idx];
    if (existingImagesRef.current.has(removedUrl)) {
      toast.info("Existing listing images are retained for audit history. Add replacement images before resubmitting.");
      return;
    }
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImageThumbnails((current) => {
      const next = { ...current };
      delete next[removedUrl];
      return next;
    });
    if (primaryIdx === idx) setPrimaryIdx(0);
    else if (primaryIdx > idx) setPrimaryIdx(primaryIdx - 1);
  };
  const moveImage = (idx, dir) => {
    const newImages = [...images];
    const target = idx + dir;
    if (target < 0 || target >= newImages.length) return;
    [newImages[idx], newImages[target]] = [newImages[target], newImages[idx]];
    setImages(newImages);
    if (primaryIdx === idx) setPrimaryIdx(target);
    else if (primaryIdx === target) setPrimaryIdx(idx);
  };

  // ---------- Document upload ----------
  const handleDocUpload = async (docKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(docKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", `document_${docKey}`);
      const { data } = await api.post("/upload", fd);
      const rawUrl = data?.url || data?.file_url || data?.public_url || data?.download_url;
      const path = prefixUrl(rawUrl);
      setDocs((prev) => ({ ...prev, [docKey]: { path, name: file.name, uploaded_at: new Date().toISOString() } }));
      toast.success(`${docKey} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingDoc("");
      e.target.value = "";
    }
  };
  const removeDoc = (key) => {
    if (existingDocsRef.current.has(key)) {
      toast.info("Existing verification documents are retained for audit history. Upload a replacement document if needed.");
      return;
    }
    setDocs((prev) => { const c = { ...prev }; delete c[key]; return c; });
  };

  // ---------- Final submit ----------
  const buildPayload = () => {
    const orderedImages = [
      images[primaryIdx],
      ...images.filter((_, i) => i !== primaryIdx),
    ].filter(Boolean);
    const payload = {
      title, description, category, listing_type: listingType,
      subcategory: subtype,
      price: Number(price),
      area: isLand ? Number(land.area) : 0,
      area_unit: isLand ? land.area_unit : "sq_yard",
      
      // Flattened location fields for backend create/update
      country: loc.country || "India",
      state: loc.state,
      district: loc.district || null,
      city: loc.city,
      sector: loc.sector || null,
      locality: loc.locality,
      address: [loc.house_number, loc.address, loc.landmark].filter(Boolean).join(", ") || null,
      pincode: loc.pincode || null,
      latitude: loc.latitude ?? null,
      longitude: loc.longitude ?? null,
      location_selection_method: locMethod,

      // Nested location object for compatibility
      location: {
        country: loc.country, state: loc.state, district: loc.district || null,
        city: loc.city, sector: loc.sector || null, locality: loc.locality,
        address: [loc.house_number, loc.address, loc.landmark].filter(Boolean).join(", ") || null,
        pincode: loc.pincode || null,
        latitude: loc.latitude ?? null, longitude: loc.longitude ?? null,
        location_selection_method: locMethod,
      },
      amenities,
      highlights: highlights.split(",").map((s) => s.trim()).filter(Boolean),
      images: orderedImages,
      posted_by: "owner",

      // Associations are hints only; the backend binds the authenticated user
      // and owns all verification flags.
      agent_user_id: (user?.role || "").toLowerCase() === "agent" ? (user?.user_id || user?.id || null) : null,
      owner_user_id: user?.user_id || user?.id || null
    };
    if (isLand) {
      payload.land_details = {
        area: Number(land.area), area_unit: land.area_unit,
        facing: land.facing,
        road_width_ft: land.road_width_ft ? Number(land.road_width_ft) : null,
        corner_plot: !!land.corner_plot,
        registry_available: !!land.registry_available,
        mutation_available: !!land.mutation_available,
        khatiyan: land.khatiyan || null, jamabandi: land.jamabandi || null,
        khasra_number: land.khasra_number || null, survey_number: land.survey_number || null,
        ownership_type: land.ownership_type,
      };
    } else {
      payload.building_details = {
        property_age_years: Number(building.property_age_years || 0),
        furnishing: building.furnishing,
        bedrooms: Number(building.bedrooms || 0),
        bathrooms: Number(building.bathrooms || 0),
        balconies: Number(building.balconies || 0),
        floor_number: building.floor_number ? Number(building.floor_number) : null,
        total_floors: building.total_floors ? Number(building.total_floors) : null,
        parking: Number(building.parking || 0),
        lift: !!building.lift,
      };
    }
    return payload;
  };

  const onSubmit = async () => {
    if (images.length < MIN_IMAGES) {
      toast.error(`Minimum ${MIN_IMAGES} images required`);
      setStep(5);
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const response = propertyId
        ? await api.put(`/v1/properties/${propertyId}`, payload)
        : await api.post("/v1/properties", payload);
      const data = response.data || {};
      const pid = data.property_id || data.id || propertyId;
      setPropertyId(pid);

      // Upload files through the media completion endpoint complete-upload or images/complete
      if (images.length) {
        for (const imgUrl of payload.images.filter((url) => !existingImagesRef.current.has(url))) {
          try {
            await api.post(`/v1/properties/${pid}/images/complete`, {
              url: imgUrl,
              thumbnail_url: imageThumbnails[imgUrl] || null,
            });
          } catch (e) {
            console.error("Failed to associate an uploaded image with the property", e);
          }
        }
      }

      // Attach docs (best-effort)
      for (const [docType, info] of Object.entries(docs)) {
        if (existingDocsRef.current.get(docType) === info.path) continue;
        try {
          await api.post(`/v1/properties/${pid}/documents`, {
            document_type: docType,
            file_path: info.path,
            url: info.path,
            file_url: info.path,
            public_url: info.path,
            file_name: info.name || `${docType}_document.pdf`,
          });
        } catch (e) { /* non-blocking */ }
      }

      // Submit for verification — surface errors so user knows status
      try {
        await api.post(`/v1/properties/${pid}/submit-verification`);
        toast.success("Property submitted for verification");
      } catch (e) {
        toast.warning(
          formatApiError(e) ||
            "Property saved as draft. Add ownership proof + 5 images to submit for verification."
        );
      }

      try {
        if (typeof refreshUser === "function") {
          await refreshUser();
        }
      } catch (err) { /* non-blocking */ }

      navigate("/dashboard/properties");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Render helpers ----------
  const performAutoReverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      if (!res.ok) throw new Error("Nominatim request failed");
      const data = await res.json();
      
      if (data && data.address) {
        const addr = data.address;
        const state = addr.state || addr.state_district || "";
        const city = addr.city || addr.town || addr.village || addr.city_district || "";
        const district = addr.district || addr.county || "";
        const pincode = addr.postcode || "";
        const locality = addr.suburb || addr.neighbourhood || addr.road || addr.village || "";
        
        let sector = "";
        const sectorRegex = /\b(Sec(?:tor)?\s+\d+\w*)\b/i;
        const searchFields = [
          data.display_name,
          addr.suburb,
          addr.neighbourhood,
          addr.road,
          addr.quarter,
          addr.subdistrict
        ];
        for (const field of searchFields) {
          if (field) {
            const match = String(field).match(sectorRegex);
            if (match) {
              sector = match[1];
              break;
            }
          }
        }

        const address = data.display_name || "";

        setLoc((p) => ({
          ...p,
          state: state || "",
          city: city || "",
          district: district || "",
          pincode: pincode || "",
          locality: locality || "",
          sector: sector || "",
          address: address || ""
        }));

        setPincodeError(!pincode);
        toast.success(`Location auto-filled: ${locality ? locality + ", " : ""}${city || ""}`);
        return;
      }
    } catch (err) {
      console.warn("Nominatim reverse geocode failed, falling back to backend API:", err);
    }

    try {
      const { data } = await api.get("/v1/locations/reverse-geocode", {
        params: { lat, lng },
      });
      if (data) {
        setLoc((p) => ({
          ...p,
          city: data.city || p.city,
          state: data.state || p.state,
          locality: data.locality || p.locality,
          district: data.district || p.district,
          pincode: data.pincode || p.pincode,
          address: data.address || p.address,
        }));
        setPincodeError(!data.pincode);
        toast.success("Address auto-filled from map");
      }
    } catch (e) {
      // ignore
    }
  };

  const reverseGeocode = () => {
    if (!loc.latitude || !loc.longitude) {
      toast.error("Select a point on the map first");
      return;
    }
    performAutoReverseGeocode(loc.latitude, loc.longitude);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeolocationError("We couldn’t detect your location. Search for your area or move the pin manually.");
      return;
    }
    setIsLocating(true);
    setGeolocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = Number(position.coords.latitude.toFixed(6));
        const lng = Number(position.coords.longitude.toFixed(6));
        setLoc((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        setLocMethod("current_location");
        setLocationConfirmed(false);
        performAutoReverseGeocode(lat, lng);
        setIsLocating(false);
        toast.success("Location detected! Click Confirm this location below the map.");
      },
      (error) => {
        setGeolocationError("We couldn’t detect your location. Search for your area or move the pin manually.");
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleMarkerDragEnd = (e) => {
    const marker = e.target;
    if (marker != null) {
      const position = marker.getLatLng();
      const flatLat = Number(position.lat.toFixed(6));
      const flatLng = Number(position.lng.toFixed(6));
      setLoc((p) => ({ ...p, latitude: flatLat, longitude: flatLng }));
      setLocMethod("map_pin");
      setLocationConfirmed(false);
      performAutoReverseGeocode(flatLat, flatLng);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`);
      if (!res.ok) throw new Error("Search request failed");
      const results = await res.json();
      if (results && results.length > 0) {
        const first = results[0];
        const lat = Number(parseFloat(first.lat).toFixed(6));
        const lng = Number(parseFloat(first.lon).toFixed(6));
        setLoc((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        setLocMethod("map_pin");
        setLocationConfirmed(false);
        performAutoReverseGeocode(lat, lng);
        toast.success("Location found on map!");
      } else {
        toast.error("Place not found. Try searching for a city, landmark or pincode.");
      }
    } catch (err) {
      toast.error("Failed to search place");
    } finally {
      setIsSearching(false);
    }
  };

  // Issue #29 (Option 1): look up an Indian PIN code and auto-fill state/district/
  // city plus a searchable list of localities. Uses the free India Post API. When
  // a locality is chosen we also try to drop the map pin on it via geocoding.
  const handlePinLookup = async (e) => {
    if (e) e.preventDefault();
    const pin = (pinLookup || "").trim();
    if (!/^\d{6}$/.test(pin)) {
      setPinError("Enter a valid 6-digit PIN code");
      return;
    }
    setPinError("");
    setPinLoading(true);
    setPinLocalities([]);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      if (!res.ok) throw new Error("PIN lookup failed");
      const data = await res.json();
      const record = Array.isArray(data) ? data[0] : null;
      if (!record || record.Status !== "Success" || !Array.isArray(record.PostOffice) || record.PostOffice.length === 0) {
        setPinError("No location found for this PIN code. Please check and try again.");
        return;
      }
      const offices = record.PostOffice;
      const first = offices[0];
      const localities = [...new Set(offices.map((o) => o.Name).filter(Boolean))];
      setPinLocalities(localities);
      setLoc((prev) => ({
        ...prev,
        pincode: pin,
        state: first.State || prev.state,
        district: first.District || prev.district,
        city: first.District || first.Block || prev.city,
        locality: localities[0] || prev.locality,
      }));
      setPincodeError(false);
      setLocMethod("pincode_search");
      // Best-effort: place the map pin using the PIN + first locality.
      try {
        const q = `${localities[0] || ""} ${pin} India`.trim();
        const geo = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
        if (geo.ok) {
          const g = await geo.json();
          if (g && g.length > 0) {
            setLoc((prev) => ({
              ...prev,
              latitude: Number(parseFloat(g[0].lat).toFixed(6)),
              longitude: Number(parseFloat(g[0].lon).toFixed(6)),
            }));
          }
        }
      } catch { /* pin placement is optional */ }
      toast.success("Location details filled from PIN code. Review and confirm below.");
    } catch (err) {
      setPinError("We couldn’t look up that PIN code right now. Please try again or use the map.");
    } finally {
      setPinLoading(false);
    }
  };

  const missingForReview = useMemo(() => {
    const m = [];
    if (!title) m.push("Title");
    if (!price) m.push("Price");
    if (!description) m.push("Description");
    if (!loc.state || !loc.city || !loc.locality) m.push("Location (state/city/locality)");
    if (!loc.latitude || !loc.longitude || !locationConfirmed) m.push("Confirmed map pin");
    if (isLand && !land.area) m.push("Land area");
    if (images.length < MIN_IMAGES) m.push(`At least ${MIN_IMAGES} images`);
    if (isLand) {
      const missingDocs = LAND_DOC_TYPES.filter((d) => !docs[d.key]).map((d) => d.label);
      if (missingDocs.length) m.push(`Required land documents: ${missingDocs.join(", ")}`);
    }
    return m;
  }, [title, price, description, loc, locationConfirmed, isLand, land, images, docs]);

  if (loadingExisting) {
    return <div className="max-w-5xl rounded-2xl border bg-card p-10 text-center" aria-live="polite">
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
      <p className="mt-3 font-bold">Loading listing draft…</p>
    </div>;
  }
  if (existingError) {
    return <div className="max-w-5xl rounded-2xl border border-destructive/30 bg-destructive/5 p-10 text-center" role="alert">
      <AlertTriangle className="mx-auto h-7 w-7 text-destructive" />
      <p className="mt-3 font-bold">Listing unavailable</p>
      <p className="mt-2 text-sm text-muted-foreground">{existingError}</p>
      <Button type="button" variant="outline" className="mt-5" onClick={() => navigate("/dashboard/properties")}>Back to properties</Button>
    </div>;
  }

  return (
    <div className="max-w-5xl" data-testid="property-wizard-page">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">
        {propertyId ? "Property Draft" : "Property Registration"}
      </p>
      <h1 className="font-display text-4xl font-black tracking-tighter uppercase text-primary mb-2">
        {propertyId ? "Edit and resubmit property" : "List a new property"}
      </h1>
      <p className="text-muted-foreground mb-8">
        {propertyId
          ? "Review the saved details, complete missing evidence, and submit the listing for verification."
          : "7-step verified listing flow. Drafts go through office & admin verification before publishing."}
      </p>

      {/* Stepper */}
      <div className="mb-10 overflow-x-auto scrollbar-none" data-testid="wizard-stepper">
        <div className="flex items-center gap-2 min-w-max">
          {STEPS.map((s, i) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={() => s.id < step && setStep(s.id)}
                  data-testid={`stepper-${s.key}`}
                  className={`flex items-center gap-2 px-1 ${s.id <= step ? "cursor-pointer" : "cursor-default"}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold transition-colors ${
                      active ? "bg-primary text-primary-foreground" :
                      done ? "bg-primary/90 text-primary-foreground" :
                      "bg-secondary text-muted-foreground"
                    }`}
                  >
                    {done ? <Check className="w-4 h-4" /> : s.id}
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-widest hidden md:inline ${
                    active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"
                  }`}>
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`w-6 md:w-10 h-px ${step > s.id ? "bg-primary" : "bg-border"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <Card className="p-6 lg:p-8 space-y-6">
        {/* ===== STEP 1: Basic Info ===== */}
        {step === 1 && (
          <div className="space-y-5" data-testid="step-basic">
            <H2>Basic Information</H2>
            <Field label="Property Title" required error={attempted ? stepErrors.basic_title : undefined}>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. 1000 sq yard plot near Yamuna Expressway"
                data-testid="input-property-title" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Property Subtype" required>
                <Select value={subtype} onValueChange={(val) => {
                  setSubtype(val);
                  const sel = SUBTYPES.find(s => s.value === val);
                  if (sel) setCategory(sel.category);
                }}>
                  <SelectTrigger data-testid="select-subtype"><SelectValue placeholder="Select property subtype" /></SelectTrigger>
                  <SelectContent>
                    {(!typeParam || typeParam === "flat") && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-primary mr-1 inline" /> Apartment / Flat</SelectLabel>
                        {SUBTYPES.filter(s => s.group === "Apartment / Flat").map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {(!typeParam || typeParam === "house") && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-1.5"><Home className="w-3.5 h-3.5 text-primary mr-1 inline" /> House / Villa</SelectLabel>
                        {SUBTYPES.filter(s => s.group === "House / Villa").map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {(!typeParam || typeParam === "land") && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-1.5"><MapIcon className="w-3.5 h-3.5 text-primary mr-1 inline" /> Plot / Land</SelectLabel>
                        {SUBTYPES.filter(s => s.group === "Plot / Land").map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Listing Type" required>
                <Select value={listingType} onValueChange={setListingType} disabled={isLand}>
                  <SelectTrigger data-testid="select-listing-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availableListingTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isLand && (
                  <p className="text-xs text-muted-foreground mt-1">Plots &amp; land are sale-only on this platform.</p>
                )}
              </Field>
            </div>
            <Field label="Price (₹)" required error={attempted ? stepErrors.basic_price : undefined} hint="Enter a positive amount (0 or more).">
              <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={price} onChange={(e) => setPrice(clampNonNegative(e.target.value))}
                placeholder="e.g. 3000000" data-testid="input-price" />
              {price > 0 && (
                <p className="text-xs text-muted-foreground mt-1">≈ {formatPrice(Number(price))}</p>
              )}
            </Field>
            <Field label="Description" required error={attempted ? stepErrors.basic_desc : undefined}>
              <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the property, neighbourhood, connectivity, etc."
                data-testid="textarea-description" />
            </Field>
            <Field label="Highlights (comma separated)">
              <Input value={highlights} onChange={(e) => setHighlights(e.target.value)}
                placeholder="e.g. Corner plot, Wide road, Clear title"
                data-testid="input-highlights" />
            </Field>
          </div>
        )}

        {/* ===== STEP 2: Location ===== */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in duration-300" data-testid="step-location">
            {!locationConfirmed ? (
              // STEP 2A: SELECT LOCATION
              <div className="space-y-5">
                <div>
                  <h2 className="font-display text-2xl font-black tracking-tight text-primary">Where is your property located?</h2>
                  <p className="text-sm text-muted-foreground mt-1">Search for an area or place the pin on the exact property location.</p>
                </div>

                {/* Issue #29: two clearly distinguishable location methods. */}
                <div className="grid sm:grid-cols-2 gap-3">
                  {/* Method 1 — Search by PIN Code */}
                  <div className={`rounded-2xl border p-4 shadow-sm transition ${locMethod === "pincode_search" ? "border-amber-400 ring-1 ring-amber-300/40 bg-amber-50/40" : "border-border/80 bg-card"}`} data-testid="loc-method-pincode">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0"><MapPin className="w-4 h-4" /></span>
                      <div>
                        <p className="text-sm font-bold text-foreground leading-none">Search by PIN Code</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Auto-fill city, district & state</p>
                      </div>
                    </div>
                    <form onSubmit={handlePinLookup} className="flex gap-2">
                      <Input
                        value={pinLookup}
                        onChange={(e) => setPinLookup(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                        inputMode="numeric"
                        placeholder="6-digit PIN"
                        className="h-10 rounded-lg font-mono"
                        data-testid="input-pincode-lookup"
                      />
                      <Button type="submit" disabled={pinLoading} className="h-10 rounded-lg font-bold px-4 shrink-0 bg-amber-600 hover:bg-amber-700">
                        {pinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fetch"}
                      </Button>
                    </form>
                    {pinError && <p className="text-[11px] text-destructive font-semibold mt-2">{pinError}</p>}
                    {pinLocalities.length > 0 && (
                      <div className="mt-3">
                        <Label className="text-[11px] font-bold text-muted-foreground">Locality</Label>
                        <select
                          value={loc.locality || ""}
                          onChange={(e) => { setLoc((prev) => ({ ...prev, locality: e.target.value })); }}
                          className="mt-1 flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                          data-testid="select-pincode-locality"
                        >
                          {pinLocalities.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <p className="text-[10px] text-muted-foreground mt-1">City, district & state stay in sync with this PIN code.</p>
                      </div>
                    )}
                  </div>

                  {/* Method 2 — Use Current Location */}
                  <div className={`rounded-2xl border p-4 shadow-sm transition ${locMethod === "current_location" ? "border-primary ring-1 ring-primary/30 bg-primary/5" : "border-border/80 bg-card"}`} data-testid="loc-method-current">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><LocateFixed className="w-4 h-4" /></span>
                      <div>
                        <p className="text-sm font-bold text-foreground leading-none">Use Current Location</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Detect via GPS (with permission)</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleUseCurrentLocation}
                      disabled={isLocating}
                      className="w-full h-10 font-bold gap-2 text-sm border-primary text-primary hover:bg-primary/5 rounded-lg"
                      data-testid="btn-use-current-location"
                    >
                      {isLocating ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Finding your location…</>
                      ) : (
                        <><Navigation className="w-4 h-4" /> Detect my location</>
                      )}
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-grow">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search area, landmark or pincode"
                      className="h-12 rounded-xl pr-10 shadow-sm border-border/80 focus-visible:ring-primary/20"
                      data-testid="input-location-search"
                    />
                    <button type="submit" disabled={isSearching} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapIcon className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button type="submit" disabled={isSearching} className="h-12 rounded-xl font-bold px-6 shadow-md shadow-primary/10">Search</Button>
                </form>

                {geolocationError && (
                  <div className="p-4 text-sm rounded-xl bg-destructive/5 text-destructive border border-destructive/10 font-semibold text-center animate-in fade-in duration-300">
                    {geolocationError}
                  </div>
                )}

                <div className="relative border border-border/80 rounded-2xl overflow-hidden shadow-sm bg-muted/10">
                  <div className="h-[360px] w-full relative z-0" data-testid="map-location-picker">
                    <MapContainer
                      center={loc.latitude && loc.longitude ? [loc.latitude, loc.longitude] : [28.6261, 77.3649]}
                      zoom={13}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        attribution='&copy; OpenStreetMap'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapClick onClick={(lat, lng) => {
                        const flatLat = Number(lat.toFixed(6));
                        const flatLng = Number(lng.toFixed(6));
                        setLoc((p) => ({ ...p, latitude: flatLat, longitude: flatLng }));
                        setLocMethod("map_pin");
                        setLocationConfirmed(false);
                        performAutoReverseGeocode(flatLat, flatLng);
                      }} />
                      {loc.latitude && loc.longitude && (
                        <>
                          <Marker
                            position={[loc.latitude, loc.longitude]}
                            icon={leafletMarkerIcon}
                            draggable={true}
                            eventHandlers={{
                              dragend: handleMarkerDragEnd
                            }}
                          />
                          <MapRecenter center={[loc.latitude, loc.longitude]} />
                        </>
                      )}
                    </MapContainer>
                  </div>
                </div>

                <p className="text-center text-xs text-muted-foreground font-semibold">
                  Move the map to place the pin on your property.
                </p>

                <Button
                  type="button"
                  onClick={() => {
                    if (!loc.latitude || !loc.longitude) {
                      toast.error("Please place the pin on the map first");
                      return;
                    }
                    setLocationConfirmed(true);
                  }}
                  className="w-full h-12 text-base font-bold shadow-lg shadow-primary/20 rounded-xl mt-2"
                  disabled={!loc.latitude || !loc.longitude}
                  data-testid="btn-confirm-location"
                >
                  Confirm this location
                </Button>
              </div>
            ) : (
              // STEP 2B: CONFIRM ADDRESS
              <div className="space-y-5">
                <div>
                  <h2 className="font-display text-2xl font-black tracking-tight text-primary">Confirm your property address</h2>
                </div>

                {/* Selected Location Summary Card */}
                <div className="p-5 rounded-2xl border border-border/80 bg-secondary/35 shadow-sm relative animate-in fade-in duration-300">
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Selected location</p>
                  <p className="text-sm font-semibold text-foreground leading-relaxed">
                    {loc.locality ? `${loc.locality}, ` : ""}
                    {loc.city ? `${loc.city}, ` : ""}
                    {loc.state ? `${loc.state}` : ""}
                    {loc.pincode ? ` – ${loc.pincode}` : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => setLocationConfirmed(false)}
                    className="mt-3 text-xs font-bold text-primary hover:underline block"
                  >
                    Change location
                  </button>
                </div>

                {pincodeError && (
                  <div className="p-3 text-xs rounded-xl bg-rose-50 text-rose-900 border border-rose-200 font-semibold animate-in slide-in-from-top flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>We couldn’t detect the pincode. Please enter it manually.</span>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Pincode" required>
                    <Input
                      value={loc.pincode}
                      onChange={(e) => setLoc({ ...loc, pincode: e.target.value })}
                      readOnly={!pincodeError && !!loc.pincode}
                      disabled={!pincodeError && !!loc.pincode}
                      placeholder={pincodeError ? "Enter pincode manually" : "Will be auto-detected"}
                      className={(!pincodeError && !!loc.pincode) ? "bg-muted/50 cursor-not-allowed font-mono font-bold" : "font-mono font-bold"}
                      data-testid="input-location-pincode"
                    />
                  </Field>
                  <Field label="City" required>
                    <Input
                      value={loc.city}
                      onChange={(e) => setLoc({ ...loc, city: e.target.value })}
                      placeholder="Ghaziabad"
                      data-testid="input-location-city"
                    />
                  </Field>
                  <Field label="Locality / Area" required>
                    <Input
                      value={loc.locality}
                      onChange={(e) => setLoc({ ...loc, locality: e.target.value })}
                      placeholder="Surya Nagar"
                      data-testid="input-location-locality"
                    />
                  </Field>
                  <Field label="House, Building or Plot Number" required>
                    <Input
                      value={loc.house_number}
                      onChange={(e) => setLoc({ ...loc, house_number: e.target.value })}
                      placeholder="Enter details"
                      data-testid="input-location-house-number"
                    />
                  </Field>
                  <Field label="Landmark — Optional" className="sm:col-span-2">
                    <Input
                      value={loc.landmark}
                      onChange={(e) => setLoc({ ...loc, landmark: e.target.value })}
                      placeholder="Enter nearby landmark"
                      data-testid="input-location-landmark"
                    />
                  </Field>
                </div>

                {/* Conditional Full Address Settings */}
                <div className="pt-2 border-t border-border/60">
                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      onClick={() => setShowFullAddress(!showFullAddress)}
                      className="text-xs font-bold text-muted-foreground hover:text-foreground"
                    >
                      {showFullAddress ? "Hide full address details" : "Edit full address"}
                    </button>
                  </div>
                  {showFullAddress && (
                    <div className="grid sm:grid-cols-3 gap-4 mt-3 p-4 rounded-2xl border border-dashed border-border/80 bg-muted/10 animate-in slide-in-from-top duration-300">
                      <Field label="Country">
                        <Input value={loc.country} onChange={(e) => setLoc({ ...loc, country: e.target.value })} />
                      </Field>
                      <Field label="State">
                        <Input value={loc.state} onChange={(e) => setLoc({ ...loc, state: e.target.value })} />
                      </Field>
                      <Field label="District">
                        <Input value={loc.district} onChange={(e) => setLoc({ ...loc, district: e.target.value })} />
                      </Field>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Field specific validation warnings */}
            {stepErrors.loc_pin && (
              <Alert variant="destructive"><AlertTriangle className="w-4 h-4"/><span>{stepErrors.loc_pin}</span></Alert>
            )}
            {stepErrors.loc_coords && (
              <Alert variant="destructive"><AlertTriangle className="w-4 h-4"/><span>{stepErrors.loc_coords}</span></Alert>
            )}
            {stepErrors.loc_pincode && (
              <Alert variant="destructive"><AlertTriangle className="w-4 h-4"/><span>{stepErrors.loc_pincode}</span></Alert>
            )}
          </div>
        )}

        {/* ===== STEP 3: Property Details (dynamic) ===== */}
        {step === 3 && (
          <div className="space-y-5" data-testid="step-details">
            <H2>Property Details {isLand ? "(Land/Plot)" : "(Building)"}</H2>
            {isLand ? (
              <>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Area" required>
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={land.area}
                      onChange={(e) => setLand({ ...land, area: clampNonNegative(e.target.value) })}
                      data-testid="input-land-area" />
                  </Field>
                  <Field label="Area Unit">
                    <Select value={land.area_unit} onValueChange={(v) => setLand({ ...land, area_unit: v })}>
                      <SelectTrigger data-testid="select-area-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {AREA_UNITS.map((u) => <SelectItem key={u} value={u}>{u.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Facing">
                    <Select value={land.facing} onValueChange={(v) => setLand({ ...land, facing: v })}>
                      <SelectTrigger data-testid="select-facing"><SelectValue /></SelectTrigger>
                      <SelectContent>{FACING_OPTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Road Width (ft)">
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={land.road_width_ft}
                      onChange={(e) => setLand({ ...land, road_width_ft: clampNonNegative(e.target.value) })}
                      data-testid="input-road-width" />
                  </Field>
                  <Field label="Ownership Type">
                    <Select value={land.ownership_type} onValueChange={(v) => setLand({ ...land, ownership_type: v })}>
                      <SelectTrigger data-testid="select-ownership"><SelectValue /></SelectTrigger>
                      <SelectContent>{OWNERSHIP.map((o) => <SelectItem key={o} value={o}>{o.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <div className="flex flex-col justify-center gap-2 pt-5">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Checkbox checked={land.corner_plot}
                        onCheckedChange={(v) => setLand({ ...land, corner_plot: !!v })}
                        data-testid="checkbox-corner-plot" />
                      Corner Plot
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Checkbox checked={land.registry_available}
                        onCheckedChange={(v) => setLand({ ...land, registry_available: !!v })}
                        data-testid="checkbox-registry" />
                      Registry Available
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Checkbox checked={land.mutation_available}
                        onCheckedChange={(v) => setLand({ ...land, mutation_available: !!v })}
                        data-testid="checkbox-mutation" />
                      Mutation Done
                    </label>
                  </div>
                </div>
                <div className="border-t border-border pt-5">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Land Records (optional)
                  </Label>
                  <div className="grid sm:grid-cols-2 gap-4 mt-3">
                    <Field label="Khatiyan No."><Input value={land.khatiyan} onChange={(e) => setLand({ ...land, khatiyan: e.target.value })} data-testid="input-khatiyan" /></Field>
                    <Field label="Jamabandi No."><Input value={land.jamabandi} onChange={(e) => setLand({ ...land, jamabandi: e.target.value })} data-testid="input-jamabandi" /></Field>
                    <Field label="Khasra No."><Input value={land.khasra_number} onChange={(e) => setLand({ ...land, khasra_number: e.target.value })} data-testid="input-khasra" /></Field>
                    <Field label="Survey No."><Input value={land.survey_number} onChange={(e) => setLand({ ...land, survey_number: e.target.value })} data-testid="input-survey" /></Field>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Bedrooms" required>
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.bedrooms} onChange={(e) => setBuilding({ ...building, bedrooms: clampNonNegative(e.target.value) })} data-testid="input-bedrooms" />
                  </Field>
                  <Field label="Bathrooms" required>
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.bathrooms} onChange={(e) => setBuilding({ ...building, bathrooms: clampNonNegative(e.target.value) })} data-testid="input-bathrooms" />
                  </Field>
                  <Field label="Balconies">
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.balconies} onChange={(e) => setBuilding({ ...building, balconies: clampNonNegative(e.target.value) })} data-testid="input-balconies" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Furnishing" required>
                    <Select value={building.furnishing} onValueChange={(v) => setBuilding({ ...building, furnishing: v })}>
                      <SelectTrigger data-testid="select-furnishing"><SelectValue /></SelectTrigger>
                      <SelectContent>{FURNISHING.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Age (years)">
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.property_age_years} onChange={(e) => setBuilding({ ...building, property_age_years: clampNonNegative(e.target.value) })} data-testid="input-age" />
                  </Field>
                  <Field label="Parking Spaces">
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.parking} onChange={(e) => setBuilding({ ...building, parking: clampNonNegative(e.target.value) })} data-testid="input-parking" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  {isApartment && (
                    <Field label="Floor Number" required>
                      <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.floor_number} onChange={(e) => setBuilding({ ...building, floor_number: clampNonNegative(e.target.value) })} data-testid="input-floor" />
                    </Field>
                  )}
                  <Field label="Total Floors" required={isApartment || isHouse}>
                    <Input type="number" min="0" onKeyDown={blockNegativeKeys} value={building.total_floors} onChange={(e) => setBuilding({ ...building, total_floors: clampNonNegative(e.target.value) })} data-testid="input-total-floors" />
                  </Field>
                  {isApartment && (
                    <div className="flex items-center pt-5">
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <Checkbox checked={building.lift} onCheckedChange={(v) => setBuilding({ ...building, lift: !!v })} data-testid="checkbox-lift" />
                        Lift Available
                      </label>
                    </div>
                  )}
                </div>
              </>
            )}
            {Object.keys(stepErrors).some(k => k.startsWith("det_")) && (
              <Alert variant="warning" data-testid="alert-details-errors">
                <AlertTriangle className="w-4 h-4"/>
                <span>Please fill required details: {Object.keys(stepErrors).filter(k => k.startsWith("det_")).map(k => stepErrors[k]).join(", ")}</span>
              </Alert>
            )}
          </div>
        )}

        {/* ===== STEP 4: Amenities ===== */}
        {step === 4 && (
          <div className="space-y-5" data-testid="step-amenities">
            <H2>Amenities &amp; Features</H2>
            <p className="text-sm text-muted-foreground">Tap chips to select amenities offered.</p>
            <div className="flex flex-wrap gap-2">
              {filteredAmenities.map((a) => {
                const active = amenities.includes(a);
                const IconComponent = AMENITY_ICONS[a];
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAmenities((p) => active ? p.filter((x) => x !== a) : [...p, a])}
                    data-testid={`chip-amenity-${a.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:bg-secondary"
                    }`}
                  >
                    {IconComponent && <IconComponent className="w-4 h-4" />}
                    <span>{a}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== STEP 5: Images ===== */}
        {step === 5 && (
          <div className="space-y-5" data-testid="step-images">
            <H2>Property Images</H2>
            <p className="text-sm text-muted-foreground">
              Upload at least <span className="font-bold text-foreground">{MIN_IMAGES} images</span>. First image (or the one you mark) will be the primary cover.
            </p>
            <label
              className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
              data-testid="dropzone-images"
            >
              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-semibold">
                {uploadingImg ? "Uploading..." : "Click to upload images"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG or GIF — multiple allowed, up to 15 MB each</p>
              <input type="file" multiple accept="image/jpeg,image/png,image/gif" className="hidden" onChange={handleImageUpload} />
            </label>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="grid-image-preview">
              {images.map((src, i) => (
                <div key={i} className="relative group rounded-md overflow-hidden border border-border">
                  <img src={src} alt={`upload-${i}`} className="w-full h-32 object-cover" />
                  {primaryIdx === i && (
                    <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground gap-1">
                      <Star className="w-3 h-3" /> Primary
                    </Badge>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <Button size="icon" variant="secondary" className="w-7 h-7" onClick={() => setPrimaryIdx(i)} data-testid={`btn-image-action-primary-${i}`} aria-label={`Make image ${i + 1} the primary image`} title="Make primary">
                      <Star className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="secondary" className="w-7 h-7" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label={`Move image ${i + 1} left`} title="Move left">
                      <ArrowLeft className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="secondary" className="w-7 h-7" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} aria-label={`Move image ${i + 1} right`} title="Move right">
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="destructive" className="w-7 h-7" onClick={() => removeImage(i)} data-testid={`btn-image-action-delete-${i}`} aria-label={`Remove image ${i + 1}`} title="Remove image">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {stepErrors.img_count && (
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <span>{stepErrors.img_count}. Currently uploaded: {images.length}.</span>
              </Alert>
            )}
          </div>
        )}

        {/* ===== STEP 6: Documents ===== */}
        {step === 6 && (
          <div className="space-y-5" data-testid="step-documents">
            <H2>{isLand ? "Land Documents (Required for Land/Plot)" : "Property Documents (Optional)"}</H2>
            <p className="text-sm text-muted-foreground">
              {isLand 
                ? "All required land documents must be uploaded before submission. Documents are reviewed by office & admin during verification."
                : "Upload registry/title deed or ownership proof to increase trust and accelerate approval."}
            </p>
            {stepErrors.doc_count && (
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <span>{stepErrors.doc_count}</span>
              </Alert>
            )}
            <div className="grid sm:grid-cols-2 gap-3" data-testid="grid-docs">
              {(isLand ? LAND_DOC_TYPES : BUILDING_DOC_TYPES).map((d) => {
                const uploaded = docs[d.key];
                return (
                  <div key={d.key}
                    className="border border-border rounded-md p-4 flex items-center justify-between gap-3"
                    data-testid={`doc-slot-${d.key}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-md grid place-items-center ${uploaded ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{d.label}</p>
                        {uploaded ? (
                          <p className="text-xs text-muted-foreground truncate">{uploaded.name}</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">Not uploaded</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      {uploaded ? (
                        <>
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Uploaded</Badge>
                          <Button size="icon" variant="ghost" className="w-7 h-7"
                            onClick={() => removeDoc(d.key)}
                            data-testid={`btn-remove-doc-${d.key}`}
                            aria-label={`Remove ${d.label || "document"}`}
                            title="Remove document"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <label
                          className="cursor-pointer inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                          data-testid={`btn-upload-doc-${d.key}`}
                        >
                          {uploadingDoc === d.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {uploadingDoc === d.key ? "Uploading" : "Upload"}
                          <input type="file" className="hidden" onChange={(e) => handleDocUpload(d.key, e)}
                            accept=".pdf,.jpg,.jpeg,.png" />
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== STEP 7: Review & Submit ===== */}
        {step === 7 && (
          <div className="space-y-5" data-testid="step-review">
            <H2>Review &amp; Submit</H2>
            {missingForReview.length > 0 && (
              <Alert variant="warning" data-testid="alert-missing-reqs">
                <AlertTriangle className="w-4 h-4" />
                <span>Please complete: <strong>{missingForReview.join(", ")}</strong></span>
              </Alert>
            )}
            <div className="space-y-4" data-testid="view-review-summary">
              <SummarySection title="Basic">
                <K v={title} k="Title" />
                <K v={SUBTYPES.find((s) => s.value === subtype)?.label || category} k="Subtype" />
                <K v={LISTING_TYPES.find((c) => c.value === listingType)?.label} k="Listing Type" />
                <K v={price ? formatPrice(Number(price)) : "—"} k="Price" />
              </SummarySection>
              <SummarySection title="Location">
                <K v={`${loc.locality}, ${loc.city}, ${loc.state}`} k="Address" />
                <K v={loc.pincode || "—"} k="Pincode" />
                <K v={loc.latitude && loc.longitude ? `${loc.latitude}, ${loc.longitude}` : "—"} k="Coordinates" />
                <K v={locationConfirmed ? "Confirmed" : "Not confirmed"} k="Map Pin" />
              </SummarySection>
              {isLand ? (
                <SummarySection title="Land Details">
                  <K v={`${land.area || "—"} ${land.area_unit.replace("_", " ")}`} k="Area" />
                  <K v={land.facing} k="Facing" />
                  <K v={land.road_width_ft ? `${land.road_width_ft} ft` : "—"} k="Road Width" />
                  <K v={land.corner_plot ? "Yes" : "No"} k="Corner Plot" />
                  <K v={land.registry_available ? "Yes" : "No"} k="Registry" />
                  <K v={land.mutation_available ? "Yes" : "No"} k="Mutation" />
                </SummarySection>
              ) : (
                <SummarySection title="Building Details">
                  <K v={building.bedrooms} k="Bedrooms" />
                  <K v={building.bathrooms} k="Bathrooms" />
                  <K v={building.furnishing} k="Furnishing" />
                  <K v={building.property_age_years + " yrs"} k="Age" />
                </SummarySection>
              )}
              <SummarySection title="Amenities">
                <div className="col-span-full flex flex-wrap gap-1.5">
                  {amenities.length ? amenities.map((a) => <Badge key={a} variant="secondary">{a}</Badge>) : <span className="text-sm text-muted-foreground">None selected</span>}
                </div>
              </SummarySection>
              <SummarySection title="Images & Documents">
                <K v={`${images.length} uploaded${images.length >= MIN_IMAGES ? " ✓" : ` (need ${MIN_IMAGES})`}`} k="Images" />
                <K v={`${Object.keys(docs).length} of ${(isLand ? LAND_DOC_TYPES : BUILDING_DOC_TYPES).length}`} k="Documents" />
              </SummarySection>
            </div>
          </div>
        )}

        {/* Nav buttons */}
        <div className="flex justify-between pt-6 border-t border-border">
          <Button type="button" variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="gap-2 font-bold"
            data-testid="btn-wizard-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          {step < STEPS.length ? (
            <Button type="button"
              onClick={handleNext}
              className="gap-2 font-bold"
              data-testid="btn-wizard-next">
              {step === 2 ? "Save and Continue" : "Next"} <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button type="button" onClick={onSubmit} disabled={submitting || missingForReview.length > 0}
              className="gap-2 font-bold"
              data-testid="btn-submit-wizard">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {submitting ? "Submitting..." : "Submit for Verification"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function H2({ children }) {
  return <h2 className="font-display text-2xl font-bold tracking-tight uppercase text-foreground">{children}</h2>;
}
function Field({ label, required, children, error, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}{required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {children}
      {/* Inline validation feedback: error takes priority over the neutral hint. */}
      {error ? (
        <p className="text-[11px] font-semibold text-destructive flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" /> {error}
        </p>
      ) : hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
function SummarySection({ title, children }) {
  return (
    <div className="bg-secondary/30 border border-border rounded-md p-4">
      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{title}</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">{children}</div>
    </div>
  );
}
function K({ k, v }) {
  return (
    <div className="flex justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-semibold text-foreground text-right">{v ?? "—"}</span>
    </div>
  );
}
function Alert({ children, variant = "warning" }) {
  const cls = variant === "warning"
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : "bg-rose-50 border-rose-300 text-rose-900";
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${cls}`}>
      {children}
    </div>
  );
}
